/**
 * RoleAccessEditor — org-admin-facing role editor.
 *
 * Exposes only the access an org admin may change: a 3-state level per workflow
 * (No access / Execute / Manage) and a 2-state data scope (Own / All) for Team
 * and Clients. Inherited org/system permissions are preserved server-side and
 * never shown here. "Manage" / "All" are disabled when the parent template does
 * not grant them (ceiling).
 */
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  WorkflowAccessLevel,
  DataScope,
  ScopedResource,
  WORKFLOW_LEVEL_RANK,
  SCOPED_RESOURCES,
  levelForWorkflow,
  scopeForResource,
} from '@upskill/shared';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { WarningToast, ToastVariant } from '../ui/WarningToast';
import roleService from '../../services/api/roleService';
import { useWorkflowDefinitions } from '../../hooks/useWorkflowDefinitions';

interface RoleAccessEditorProps {
  roleId: string;
  /** Current permission keys on the role (derives initial levels/scopes). */
  roleKeys: string[];
  /** Parent template permission keys (defines the ceiling). */
  templateKeys: string[];
  onSaved: () => void;
}

const WORKFLOW_LEVEL_OPTIONS: { value: WorkflowAccessLevel; label: string }[] = [
  { value: 'none', label: 'No access' },
  { value: 'execute', label: 'Execute' },
  { value: 'manage', label: 'Manage' },
];

const SCOPE_OPTIONS: { value: DataScope; label: string }[] = [
  { value: 'none', label: 'No access' },
  { value: 'own', label: 'Own' },
  { value: 'all', label: 'All' },
];

const SCOPED_ROWS: { key: ScopedResource; label: string }[] = [
  { key: 'team', label: 'Team' },
  { key: 'clients', label: 'Clients' },
  { key: 'locations', label: 'Centers' },
];

export const RoleAccessEditor = ({ roleId, roleKeys, templateKeys, onSaved }: RoleAccessEditorProps) => {
  const queryClient = useQueryClient();
  const { data: workflowDefs, isLoading } = useWorkflowDefinitions();

  const templateKeySet = useMemo(() => new Set(templateKeys), [templateKeys]);

  // Only workflows the parent template grants access to are shown. The platform
  // enables a workflow for orgs by granting it to the Org Admin role template;
  // org admins then build custom roles within the workflows their org has.
  const activeWorkflows = useMemo(
    () =>
      (workflowDefs ?? []).filter(
        (d) => d.isActive && levelForWorkflow([...templateKeySet], d.code) !== 'none',
      ),
    [workflowDefs, templateKeySet],
  );

  // Initial state derived from the role's current keys.
  const [workflows, setWorkflows] = useState<Record<string, WorkflowAccessLevel>>({});
  const [scopes, setScopes] = useState<Record<ScopedResource, DataScope>>(() => ({
    team: scopeForResource(roleKeys, 'team'),
    clients: scopeForResource(roleKeys, 'clients'),
    locations: scopeForResource(roleKeys, 'locations'),
  }));

  // Transient save feedback toast (auto-dismisses).
  const [toast, setToast] = useState<{ message: string; variant: ToastVariant } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Seed workflow state once definitions arrive.
  useEffect(() => {
    const seed: Record<string, WorkflowAccessLevel> = {};
    for (const wf of activeWorkflows) seed[wf.code] = levelForWorkflow(roleKeys, wf.code);
    setWorkflows(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkflows.length]);

  const mutation = useMutation({
    mutationFn: () =>
      roleService.updateRoleAccess(roleId, {
        workflows,
        scopes,
      }),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not save access. Please try again.';
      setToast({ message: msg, variant: 'error' });
    },
    onSuccess: () => {
      setToast({ message: 'Access updated', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['role', roleId] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      onSaved();
    },
  });

  /** Max level the template allows for a workflow (the ceiling). */
  const ceilingLevel = (code: string): WorkflowAccessLevel =>
    levelForWorkflow([...templateKeySet], code);

  /** Whether a scope option is allowed for a resource by the parent template. */
  const scopeOptionAllowed = (r: ScopedResource, value: DataScope): boolean => {
    if (value === 'none') return true;
    if (value === 'own') return templateKeySet.has(SCOPED_RESOURCES[r].base);
    return templateKeySet.has(SCOPED_RESOURCES[r].all);
  };

  if (isLoading) return <Card style={{ padding: 24 }}>Loading access…</Card>;

  return (
    <Wrapper>
      <WarningToast
        message={toast?.message ?? null}
        variant={toast?.variant}
        onDismiss={() => setToast(null)}
      />
      <Section>
        <SectionTitle>Workflow Access</SectionTitle>
        <SectionHint>Choose what this role can do in each workflow.</SectionHint>
        {activeWorkflows.length === 0 && (
          <Muted>No workflows are available for your organization yet.</Muted>
        )}
        {activeWorkflows.map((wf) => {
          const ceil = ceilingLevel(wf.code);
          return (
            <Row key={wf.code}>
              <RowLabel>{wf.name}</RowLabel>
              <Segmented>
                {WORKFLOW_LEVEL_OPTIONS.map((opt) => {
                  const disabled = WORKFLOW_LEVEL_RANK[opt.value] > WORKFLOW_LEVEL_RANK[ceil];
                  const active = (workflows[wf.code] ?? 'none') === opt.value;
                  return (
                    <SegBtn
                      key={opt.value}
                      type="button"
                      $active={active}
                      disabled={disabled}
                      title={disabled ? 'Not allowed by the base template' : undefined}
                      onClick={() => setWorkflows((w) => ({ ...w, [wf.code]: opt.value }))}
                    >
                      {opt.label}
                    </SegBtn>
                  );
                })}
              </Segmented>
            </Row>
          );
        })}
      </Section>

      <Section>
        <SectionTitle>Data Access</SectionTitle>
        <SectionHint>Limit this role to its own records or the whole organization.</SectionHint>
        {SCOPED_ROWS.map((row) => (
          <Row key={row.key}>
            <RowLabel>{row.label}</RowLabel>
            <Segmented>
              {SCOPE_OPTIONS.map((opt) => {
                const disabled = !scopeOptionAllowed(row.key, opt.value);
                return (
                  <SegBtn
                    key={opt.value}
                    type="button"
                    $active={scopes[row.key] === opt.value}
                    disabled={disabled}
                    title={disabled ? 'Not allowed by the base template' : undefined}
                    onClick={() => setScopes((s) => ({ ...s, [row.key]: opt.value }))}
                  >
                    {opt.label}
                  </SegBtn>
                );
              })}
            </Segmented>
          </Row>
        ))}
      </Section>

      <ActionBar>
        <Button variant="primary-blue" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save Access'}
        </Button>
      </ActionBar>
    </Wrapper>
  );
};

export default RoleAccessEditor;

// ==================== Styled ====================

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const Section = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[5]};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionHint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Muted = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
  min-height: 40px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 0;
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const SegBtn = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border: none;
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary.base : theme.colors.backgrounds.white)};
  color: ${({ $active, theme }) => ($active ? theme.colors.text.white : theme.colors.text.primary)};
  cursor: pointer;

  &:first-child {
    border-left: none;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
`;
